import { useState } from "react";
import { toast } from "sonner";
import { Plus, Users, Pencil, Trash2, FileDown, Send, History, KeyRound } from "lucide-react";
import { WelcomeResultDialog } from "@/components/dialogs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Loading } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { api, useFetch, errMsg, downloadFile } from "@/lib/api";
import { StatementHistory } from "@/components/StatementHistory";

const FIELDS = [
  { name: "name", label: "Landlord name", required: true, full: true },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone" },
  { name: "address", label: "Correspondence address", full: true },
  { name: "fee_percent", label: "Management fee (%)", type: "number", help: "Used on landlord statements" },
  { name: "notes", label: "Notes", type: "textarea" },
];

export default function Landlords() {
  const { data, reload } = useFetch("/biz/landlords");
  const [edit, setEdit] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [hist, setHist] = useState(null);
  const [welcome, setWelcome] = useState(null);
  const invite = async (l) => {
    if (!window.confirm(`Send ${l.name} a landlord portal login? ${l.portal_user_id ? "This resets their password." : ""}`)) return;
    try { const r = await api.post(`/biz/landlords/${l.id}/invite`, { origin_url: window.location.origin }); setWelcome(r.data); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const months = Array.from({ length: 24 }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
    return { value: d.toISOString().slice(0, 7), label: d.toLocaleDateString("en-GB", { month: "long", year: "numeric" }) };
  });
  const lastMonth = months[1].value;
  const del = async (l) => {
    if (!window.confirm(`Delete ${l.name}?`)) return;
    try { await api.delete(`/biz/landlords/${l.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="landlords-page">
      <PageHeader eyebrow="Clients" title="Landlords" subtitle="The owners you manage properties for."
        actions={<Button onClick={() => setEdit({})} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-landlord-button"><Plus className="w-4 h-4 mr-1" />Add landlord</Button>} />
      {!data ? <Loading /> : data.length === 0 ? (
        <EmptyState icon={Users} title="No landlords yet" text="Add the landlords you work for, then assign properties to them." />
      ) : (
        <div className="panel p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Properties</TableHead><TableHead>Fee</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.map((l) => (
                <TableRow key={l.id} data-testid={`landlord-row-${l.id}`}>
                  <TableCell className="font-semibold">{l.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{l.email}<div className="text-xs">{l.phone}</div></TableCell>
                  <TableCell>{l.property_count}</TableCell>
                  <TableCell>{l.fee_percent}%</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" className="mr-1" onClick={() => setStmt({ ...l, mode: "download" })} data-testid={`statement-landlord-${l.id}`}><FileDown className="w-4 h-4 mr-1" />Statement</Button>
                    <span title={l.email ? "" : "Add an email for this landlord first"} className="inline-block"><Button variant="outline" size="sm" className="mr-1" onClick={() => setStmt({ ...l, mode: "send" })} disabled={!l.email} data-testid={`send-statement-landlord-${l.id}`}><Send className="w-4 h-4 mr-1" />Send to landlord</Button></span>
                    <Button variant="ghost" size="sm" className="mr-1" onClick={() => setHist(l)} data-testid={`history-landlord-${l.id}`}><History className="w-4 h-4 mr-1" />History</Button>
                    <Button variant="ghost" size="sm" className="mr-1" onClick={() => invite(l)} disabled={!l.email} data-testid={`invite-landlord-${l.id}`}><KeyRound className="w-4 h-4 mr-1" />{l.portal_user_id ? "Resend login" : "Send login"}</Button>
                    <Button variant="ghost" size="icon" onClick={() => setEdit(l)} data-testid={`edit-landlord-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(l)} data-testid={`delete-landlord-${l.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <WelcomeResultDialog welcome={welcome} onClose={() => setWelcome(null)} />
      <StatementHistory landlord={hist} onClose={() => setHist(null)} />
      <FormDialog open={!!stmt} onOpenChange={(o) => !o && setStmt(null)} title={stmt?.mode === "send" ? "Email statement to landlord" : "Landlord statement"} testid="statement-form"
        description={stmt ? (stmt.mode === "send" ? `The PDF statement will be emailed to ${stmt.name} at ${stmt.email}.` : `PDF statement for ${stmt.name}: rent received, costs, your ${stmt.fee_percent}% fee and net payable.`) : ""}
        initial={{ month: lastMonth }} submitLabel={stmt?.mode === "send" ? "Send statement" : "Download PDF"}
        fields={[{ name: "month", label: "Month", type: "select", required: true, full: true, options: months }]}
        onSubmit={async (v) => {
          if (stmt.mode === "send") {
            const r = await api.post(`/biz/landlords/${stmt.id}/statement/send`, { month: v.month });
            if (r.data.status === "sent") toast.success(`Statement emailed to ${r.data.to}`);
            else toast.warning("Email isn't set up yet — ask your administrator to configure SMTP. You can download the PDF instead.");
            reload();
          } else {
            await downloadFile(`/biz/landlords/${stmt.id}/statement?month=${v.month}`, `statement-${stmt.name.replace(/\s+/g, "-").toLowerCase()}-${v.month}.pdf`);
            toast.success("Statement downloaded");
          }
        }} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit landlord" : "Add landlord"} testid="landlord-form"
        fields={FIELDS} initial={edit}
        onSubmit={async (v) => {
          if (edit?.id) await api.put(`/biz/landlords/${edit.id}`, v); else await api.post("/biz/landlords", v);
          toast.success("Landlord saved"); reload();
        }} />
    </div>
  );
}
